import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, Textarea, SelectNative } from "../ui/input";
import { ImageUpload } from "../subcomponents/ImageUpload";

type ProgramFormState = {
  name: string;
  description: string;
  coverImage: string;
  facultyId: string;
  isActive: boolean;
};

const EMPTY_PROGRAM: ProgramFormState = {
  name: "",
  description: "",
  coverImage: "",
  facultyId: "",
  isActive: true,
};

type Props = {
  open: boolean;
  editing: any | null;
  faculties: any[];
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "special-program-form";

export const AdminSpecialProgramModal: React.FC<Props> = ({ open, editing, faculties, onClose, onSaved }) => {
  const { t } = useLanguage();

  const schema = Yup.object({
    name: Yup.string().trim().required(t.common.required),
    facultyId: Yup.string().required(t.common.required),
    description: Yup.string(),
    coverImage: Yup.string(),
  });

  const initial: ProgramFormState = editing
    ? {
        name: editing.name || "",
        description: editing.description || "",
        coverImage: editing.coverImage || "",
        facultyId: editing.facultyId ? String(editing.facultyId) : "",
        isActive: editing.isActive === undefined ? true : !!editing.isActive,
      }
    : EMPTY_PROGRAM;

  const title = editing
    ? `${t.admin.edit} — ${t.admin.specialPrograms}`
    : `${t.admin.create} — ${t.admin.specialPrograms}`;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const payload = {
          name: values.name.trim(),
          description: values.description.trim(),
          coverImage: values.coverImage,
          facultyId: Number(values.facultyId),
          isActive: values.isActive,
        };
        try {
          if (editing) {
            await apiPut(`/special-programs/${editing.id}`, payload);
          } else {
            await apiPost("/special-programs", payload);
          }
          toast.success(t.common.saveSuccess);
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={title}
          subtitle={t.admin.specialProgramsSubtitle}
          testId="special-program-modal"
          className="max-w-2xl"
          formId={FORM_ID}
          submitLabel={t.admin.save}
          isSubmitting={isSubmitting}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <AdminField label={t.admin.specialProgramName} required error={touched.name ? errors.name : undefined}>
              <Field as={Input} name="name" data-testid="form-program-name" />
            </AdminField>
            <AdminField label={t.admin.faculty} required error={touched.facultyId ? errors.facultyId : undefined}>
              <Field as={SelectNative} name="facultyId" data-testid="form-program-faculty">
                <option value="">{t.admin.selectFaculty}</option>
                {faculties.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </Field>
            </AdminField>
            <ImageUpload
              value={values.coverImage}
              onChange={(url) => setFieldValue("coverImage", url)}
              folder="ulpgl/programs"
              label={t.admin.coverImage}
              testId="program-cover-image"
            />
            <AdminField label={t.admin.description}>
              <Field as={Textarea} name="description" rows={3} data-testid="form-program-description" />
            </AdminField>
            <AdminField label={t.admin.status}>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
                <Field type="checkbox" name="isActive" data-testid="form-program-active" />
                {t.admin.specialProgramActive}
              </label>
            </AdminField>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
